import React, { useMemo, useState } from 'react';

import { createList, useGetLists } from '@vlabs/api/artemy/client';
import type { DepartmentId } from '@vlabs/api/artemy/model';
import { InfoCircleIcon, CrossIcon } from '@vlabs/icons';
import validate from '@vlabs/shared/validators';
import { Control } from '@vlabs/uikit';
import type { TOption, WithDataTestid } from '@vlabs/uikit/types';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { OwnerDepartmentSelect } from '@vlabs/monokit/shared/ui/owner-department-select';

import st from './AddCardsToListForm.module.sass';
import { useAddCardsToListFormTranslation } from './i18n';

interface AddCardsToListFormValues {
  listId: string | TOption;
  ownerDepartmentId: DepartmentId;
}

interface AddCardsToListFormProps extends WithDataTestid {
  onSubmit: (listId: string) => Promise<void>;
  onCancel: VoidFunction;
  context?: 'add' | 'move';
}

const displayName = 'AddCardsToListForm';
const testid = buildTestid(displayName);

export function AddCardsToListFormPoint3({
  onSubmit,
  onCancel,
  context = 'add',
  'data-testid': dataTestId = 'undefined',
}: AddCardsToListFormProps): React.JSX.Element {
  const { t } = useAddCardsToListFormTranslation();
  const { data: listsData } = useGetLists({ page_size: 1000 });
  const lists = useMemo(() => (listsData?.lists ?? []).map(({ list_id, user_data }) => ({
    value: list_id,
    label: user_data || list_id,
  })), [listsData?.lists]);

  const [mode, setMode] = useState<'select' | 'create'>('select');

  const {
    control,
    register,
    watch,
    reset,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<AddCardsToListFormValues>({ defaultValues: { listId: '', ownerDepartmentId: '' } });

  const listValue = watch('listId');
  const departmentValue = watch('ownerDepartmentId');

  const submitForm = async ({ listId, ownerDepartmentId }: AddCardsToListFormValues) => {
    if (mode === 'create') {
      if (typeof listId !== 'string' || !listId) {
        return;
      }

      try {
        const createResponse = await createList({
          user_data: listId,
          owner_department: ownerDepartmentId,
          is_active: true,
          is_open: true,
        });

        await onSubmit(createResponse.list_id);
      } catch {
        toast.error(t('toast_create_list_error'));
      }

      return;
    }

    if (listId && typeof listId !== 'string') {
      await onSubmit(listId.value);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(submitForm)}
      {...testid(dataTestId)}
    >
      <div className={st.Root}>
        <div className={st.Header}>
          <span className={st.Title}>{t('title', { context })}</span>
          {context === 'move' && (
            <span className={st.Subtitle}>
              <InfoCircleIcon className={st.SubtitleIcon} />
              {t('subtitle')}
            </span>
          )}
        </div>

        <div className={st.ListsOptions}>
          <Controller
            control={control}
            name="listId"
            render={({ field }) => (
              <Control.Select
                key={mode}
                {...field}
                disabled={mode === 'create'}
                id="lists"
                options={lists}
                placeholder={t('select_placeholder')}
                value={typeof field.value === 'string' ? undefined : field.value}
                {...testid('ExistingListsSelect', dataTestId)}
              />
            )}
          />
          {mode === 'select' && (
            <Control.Button
              className={st.CreateListButton}
              kind="primary"
              onClick={() => {
                setMode('create');
                reset({ listId: '', ownerDepartmentId: '' });
              }}
              variant="flat"
              {...testid('CreateListSwitchModeButton', dataTestId)}
            >
              {t('new_list_button')}
            </Control.Button>
          )}
          {mode === 'create' && (
            <React.Fragment>
              <div className={st.CreateNewList}>
                <div className={st.InputWrapper}>
                  <Control.Input
                    {...register('listId', { maxLength: validate.maxLength(128) })}
                    errors={errors}
                    id="newListName"
                    label={t('input_label')}
                    placeholder={t('input_placeholder')}
                    required
                    {...testid('CreateNewListInput', dataTestId)}
                  />
                </div>
                <Control.IconButton
                  className={st.IconButton}
                  icon={<CrossIcon />}
                  kind="negative"
                  onClick={() => {
                    setMode('select');
                    reset({ listId: '', ownerDepartmentId: '' });
                  }}
                  variant="flat"
                  {...testid('SwitchModeButton', dataTestId)}
                />
              </div>
              <div className={st.SelectWrapper}>
                <OwnerDepartmentSelect
                  control={control}
                  errors={errors}
                  label={t('department_label')}
                  name="ownerDepartmentId"
                  required
                  setValue={setValue}
                />
              </div>

            </React.Fragment>
          )}
        </div>

        <div className={st.Actions}>
          <Control.Button
            className={st.Action}
            disabled={!listValue || (mode === 'create' && !departmentValue)}
            kind="primary"
            type="submit"
            variant="filled"
            {...testid('SubmitButton', dataTestId)}
          >
            {t('submit_button', { context })}
          </Control.Button>
          <Control.Button
            className={st.Action}
            kind="secondary"
            onClick={onCancel}
            variant="outlined"
            {...testid('CloseButton', dataTestId)}
          >
            {t('cancel_button')}
          </Control.Button>
        </div>
      </div>
    </form>
  );
}

AddCardsToListFormPoint3.displayName = displayName;
